import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Input, Select as AntSelect, Spin } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { CropImagePreview } from 'components/reusables/cropImagePreview'
import Select from 'components/reusables/select'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { slices } from 'store/slices'
import { LearningPathStatus } from 'enum/learningPathStatus.enum'
import { learningPathsService } from 'api/administration/learningPaths'
import { learningPathStates } from 'components/reusables/states'
import { ProfileTag, StateAlert } from './styled'
import { Title, Subtitle } from './../styled'

const { Option } = AntSelect

const Configuration = () => {
  const { t } = useTranslation()
  const prefix = 'administration.learningPaths.manage.create.configuration'
  const dispatch = useAppDispatch()
  const params = useParams<{ id: string }>()
  const [showStateAlert, setShowStateAlert] = useState(false)
  const profiles = useAppSelector(slices.profiles.items)
  const loadingProfiles = useAppSelector(slices.profiles.loading)
  const loadedProfiles = useAppSelector(slices.profiles.loaded)
  const configuration = useAppSelector(slices.learningPath.configuration)
  const loadingConfiguration = useAppSelector(
    slices.learningPath.loadingConfiguration
  )

  useEffect(() => {
    if (!loadedProfiles && !loadingProfiles) getProfiles()
  }, [])

  const getProfiles = async () => {
    dispatch(slices.profiles.setLoading(true))
    await learningPathsService.getProfiles().then((response) => {
      dispatch(slices.profiles.setItems(response))
    })
    dispatch(slices.profiles.setLoading(false))
  }

  const onChangeState = (value: number) => {
    dispatch(slices.learningPath.setState(value))
    setShowStateAlert(value === LearningPathStatus.draft)
  }

  return (
    <Spin spinning={loadingConfiguration}>
      <Title>{t(`${prefix}.title`)}</Title>
      <Subtitle $required>{t(`${prefix}.inputs.name.title`)}</Subtitle>
      <Input
        value={configuration.name}
        onChange={(event) =>
          dispatch(slices.learningPath.setName(event.target.value))
        }
        autoFocus
      />
      <Subtitle>{t(`${prefix}.inputs.description.title`)}</Subtitle>
      <Input.TextArea
        value={configuration.description}
        onChange={(event) =>
          dispatch(slices.learningPath.setDescription(event.target.value))
        }
        autoSize={{ minRows: 2, maxRows: 2 }}
      />
      <Subtitle>{t(`${prefix}.inputs.image.title`)}</Subtitle>
      <CropImagePreview
        name="image"
        image={configuration.image}
        setImage={(_input: string, value: any) =>
          dispatch(slices.learningPath.setImage(value))
        }
      />
      <Subtitle>{t(`${prefix}.inputs.profiles.title`)}</Subtitle>
      <Spin spinning={loadingProfiles}>
        <Select
          mode="multiple"
          showSearch
          placeholder={t(`${prefix}.inputs.profiles.placeholder`)}
          value={configuration.profiles}
          onSelect={(value) =>
            dispatch(
              slices.learningPath.setProfiles([
                ...configuration.profiles,
                value,
              ])
            )
          }
          onDeselect={(value) =>
            dispatch(
              slices.learningPath.setProfiles(
                configuration.profiles.filter((item) => item !== value)
              )
            )
          }
          optionFilterProp="children"
          filterOption={(input, option) => {
            const title = option.props.title

            if (!title) return false

            return title.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }}
        >
          {profiles.map((profile) => (
            <Option key={profile.id} value={profile.id} title={profile.name}>
              <ProfileTag color={profile.color}>
                {profile.name}
                <FontAwesomeIcon
                  icon={faTimes as IconProp}
                  onClick={() =>
                    dispatch(
                      slices.learningPath.setProfiles(
                        configuration.profiles.filter(
                          (item) => item !== profile.id
                        )
                      )
                    )
                  }
                />
              </ProfileTag>
            </Option>
          ))}
        </Select>
      </Spin>
      {params.id && (
        <>
          <Subtitle>{t(`${prefix}.inputs.state.title`)}</Subtitle>
          <Select
            value={configuration.state}
            onSelect={(value) => onChangeState(value)}
          >
            {learningPathStates.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.component}
              </Option>
            ))}
          </Select>
          {showStateAlert && (
            <StateAlert>
              <FontAwesomeIcon icon={faInfoCircle} />
              {t(`${prefix}.inputs.state.alert`)}
            </StateAlert>
          )}
        </>
      )}
    </Spin>
  )
}

export default Configuration
